import React from 'react';
import { Box, Stack, Typography, useMediaQuery, useTheme } from '@mui/material';

import {
  PowerIcon,
  TurboIcon,
  LightIcon,
  FanIcon,
  ClapetIcon,
  BurnerIcon,
} from '../../../theme/icons';
import { ActionExtendedValue } from '../../../redux/controlUnit/selectors/actions';
import Status from '../DisplayValues/StatusOven';
import { useCurrentStateByControlUnitId } from '../../../redux/controlUnit/hooks/useCurrentStateByControlUnitId';
import { getPolyglot } from '../../../i18n';
import ModeOven from '../DisplayValues/ModeOven';

interface DeckActionsStatusProps {
  deviceId: string;
  controlUnitId: string;
  on?: ActionExtendedValue;
  light?: ActionExtendedValue;
  clapet?: ActionExtendedValue;
  burner?: ActionExtendedValue;
  multiFunction?: ActionExtendedValue;
  fan?: ActionExtendedValue;
  CUTranslate: ReturnType<typeof useCurrentStateByControlUnitId>['CUTranslate'];
  isDeviceConnected?: boolean;
}

function DeckActionsStatus({
  deviceId,
  controlUnitId,
  on,
  light,
  clapet,
  burner,
  multiFunction,
  fan,
  isDeviceConnected,
  CUTranslate,
}: DeckActionsStatusProps) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const polyglot = getPolyglot();
  return (
    <Stack
      justifyContent={isMobile ? 'center' : 'flex-end'}
      flexWrap={'wrap'}
      direction={'row'}
      justifyItems={'center'}
      alignItems={'center'}
      gap={2}
    >
      {on && (
        <Status
          value={on?.value}
          icon={<PowerIcon height={'1em'} />}
          name={
            on?.value
              ? polyglot.t('composable_device.power_on')
              : polyglot.t('composable_device.power_off')
          }
          deviceId={deviceId}
          controlUnitId={controlUnitId}
          actionId={on?.metadata.id}
          CUTranslate={CUTranslate}
          isDeviceConnected={isDeviceConnected}
          switchColors={{
            buttonColor: on?.value
              ? theme.palette.success.main
              : theme.palette.grey[400],
            trackColorOn: isDeviceConnected
              ? theme.palette.success.light
              : theme.palette.grey[400],
            iconColor: isDeviceConnected
              ? theme.palette.success.contrastText
              : theme.palette.grey[400],
            trackColorOff: theme.palette.grey[600],
            thumbColorOff: theme.palette.grey[200],
          }}
        />
      )}
      {light && (
        <Status
          value={light?.value}
          icon={<LightIcon height={'1em'} />}
          name={
            light?.value
              ? polyglot.t('composable_device.light_on')
              : polyglot.t('composable_device.light_off')
          }
          deviceId={deviceId}
          controlUnitId={controlUnitId}
          actionId={light?.metadata.id}
          CUTranslate={CUTranslate}
          isDeviceConnected={isDeviceConnected}
        />
      )}
      {fan && (
        <Status
          value={fan?.value}
          icon={<FanIcon height={'1em'} />}
          name={
            fan?.value
              ? polyglot.t('composable_device.fan_on')
              : polyglot.t('composable_device.fan_off')
          }
          deviceId={deviceId}
          controlUnitId={controlUnitId}
          actionId={fan?.metadata.id}
          CUTranslate={CUTranslate}
          isDeviceConnected={isDeviceConnected}
        />
      )}
      {clapet && (
        <Status
          value={clapet?.value}
          icon={<ClapetIcon height={'1em'} />}
          name={
            clapet?.value
              ? polyglot.t('composable_device.clapet_on')
              : polyglot.t('composable_device.clapet_off')
          }
          deviceId={deviceId}
          controlUnitId={controlUnitId}
          actionId={clapet?.metadata.id}
          CUTranslate={CUTranslate}
          isDeviceConnected={isDeviceConnected}
        />
      )}
      {!isMobile && (burner || multiFunction) && (
        <Box
          height={'50px'}
          borderRight={(theme) => `2px solid ${theme.palette.divider}`}
        ></Box>
      )}
      {(burner || multiFunction) && (
        <Box>
          <Typography fontSize={'0.9rem'} textAlign="start">
            Mode
          </Typography>
          <Box display="flex" flexDirection="row" alignItems="center">
            {burner && (
              <ModeOven
                value={burner?.value}
                icon={
                  <BurnerIcon
                    fill={isDeviceConnected ? 'primary' : 'disabled'}
                    height={'1em'}
                  />
                }
                name={polyglot.t('composable_device.burner')}
                deviceId={deviceId}
                controlUnitId={controlUnitId}
                actionId={burner?.metadata.id}
                CUTranslate={CUTranslate}
                isDeviceConnected={isDeviceConnected}
              />
            )}
            {multiFunction && (
              <ModeOven
                value={multiFunction?.value}
                icon={<TurboIcon height={'1em'} />}
                name={polyglot.t('composable_device.turbo')}
                deviceId={deviceId}
                controlUnitId={controlUnitId}
                actionId={multiFunction?.metadata.id}
                CUTranslate={CUTranslate}
                isDeviceConnected={isDeviceConnected}
              />
            )}
          </Box>
        </Box>
      )}
    </Stack>
  );
}

export default DeckActionsStatus;
